/**
 * encoder.js — FFmpeg-WASM encoding for video-bg-remover.
 *
 * All alpha encoding goes through FFmpeg WASM (the WebCodecs encoder is H.264
 * only and forces alpha opaque). Five output formats:
 *
 *   webm-alpha  VP8 + yuva420p           transparent, plays in Chrome/FF/Edge/Android/Safari
 *   prores      ProRes 4444 + yuva444p   transparent, for Premiere/DaVinci/FCP
 *   mp4         H.264 + yuv420p          opaque, composited background baked in
 *   apng        APNG                     transparent "video PNG" for <img> embedding
 *   webp        animated WebP            transparent, smaller than APNG
 *
 * Memory: video formats are encoded in CHUNK-sized SEGMENTS. The pipeline
 * writes a chunk of frame PNGs, the encoder transcodes them to one segment and
 * deletes the PNGs — so the FFmpeg FS never holds more than ~CHUNK frames.
 * Segments are concatenated (stream copy) and audio is muxed in a final pass.
 * APNG/WebP are single-pass (their duration is capped, so the frame count is).
 *
 * Public API — window.VbrEncoder:
 *   adaptiveBitrate(w, h, fps)        → kbps   (pure)
 *   formatInfo(format)               → { ext, mime, segmented, … }  (pure)
 *   segmentArgs(format, o)           → string[]  (pure — node-testable)
 *   singlePassArgs(format, o)        → string[]  (pure)
 *   concatArgs(format)               → string[]  (pure)
 *   muxArgs(format, o)               → string[]  (pure)
 *   createSession(proc, opts)        → encoder session (see below)
 *
 * session: addFrame(canvas) · flush() · finalize(onProgress) → { blob, name }
 *          · cleanup()
 */
(function (root) {
    'use strict';

    var CHUNK = 90;                 // frames per segment — bounds FFmpeg FS heap

    /* ───────────────────────── pure helpers ───────────────────────── */

    function adaptiveBitrate(w, h, fps) {
        var kbps = (w * h * fps * 0.10) / 1000;     // ~0.10 bits per pixel
        return Math.round(Math.max(1000, Math.min(12000, kbps)));
    }

    var FORMATS = {
        'webm-alpha': { ext: 'webm', segExt: 'webm', mime: 'video/webm',     segmented: true,  audio: 'libopus'   },
        'prores':     { ext: 'mov',  segExt: 'mov',  mime: 'video/quicktime', segmented: true,  audio: 'pcm_s16le' },
        'mp4':        { ext: 'mp4',  segExt: 'mp4',  mime: 'video/mp4',       segmented: true,  audio: 'aac'       },
        'apng':       { ext: 'png',  segExt: '',     mime: 'image/apng',      segmented: false, audio: null        },
        'webp':       { ext: 'webp', segExt: '',     mime: 'image/webp',      segmented: false, audio: null        },
    };

    function formatInfo(format) { return FORMATS[format] || FORMATS['webm-alpha']; }

    function videoCodecArgs(format, bitrate) {
        if (format === 'prores') {
            return ['-c:v', 'prores_ks', '-profile:v', '4444',
                    '-pix_fmt', 'yuva444p10le', '-alpha_bits', '16', '-vendor', 'apl0'];
        }
        if (format === 'mp4') {
            return ['-c:v', 'libx264', '-pix_fmt', 'yuv420p',
                    '-crf', '20', '-preset', 'medium'];
        }
        // webm-alpha — VP8, not VP9: libvpx-vp9 encoding traps the WASM build
        // (verified — even opaque VP9 crashes), while VP8 + alpha is solid and
        // the most broadly-compatible transparent-video format.
        return ['-c:v', 'libvpx', '-pix_fmt', 'yuva420p',
                '-b:v', bitrate + 'k', '-auto-alt-ref', '0'];
    }

    /** Encode one chunk of per-chunk-numbered PNGs (f%05d.png) → a video-only segment. */
    function segmentArgs(format, o) {
        return ['-framerate', String(o.fps), '-i', 'f%05d.png']
            .concat(videoCodecArgs(format, o.bitrate))
            .concat(['-an', '-y', o.output]);
    }

    /** Single-pass encode of global-numbered PNGs (f%05d.png) → APNG / animated WebP. */
    function singlePassArgs(format, o) {
        var args = ['-framerate', String(o.fps), '-i', 'f%05d.png'];
        if (format === 'apng') {
            args = args.concat(['-plays', '0', '-f', 'apng']);
        } else {
            args = args.concat(['-c:v', 'libwebp_anim', '-loop', '0',
                                 '-lossless', '0', '-q:v', '80', '-pix_fmt', 'yuva420p']);
        }
        return args.concat(['-y', o.output]);
    }

    /** Concatenate video segments (stream copy) → one silent file. */
    function concatArgs(format) {
        var info = formatInfo(format);
        return ['-f', 'concat', '-safe', '0', '-i', 'concat.txt',
                '-c', 'copy', '-an', '-y', 'silent.' + info.ext];
    }

    /** Mux trimmed source audio onto the silent video → final file. */
    function muxArgs(format, o) {
        var info = formatInfo(format);
        return ['-i', 'silent.' + info.ext,
                '-ss', String(o.trimStart), '-t', String(o.duration), '-i', 'source_video',
                '-map', '0:v:0', '-map', '1:a:0?',
                '-c:v', 'copy', '-c:a', info.audio]
            .concat(info.audio === 'pcm_s16le' ? [] : ['-b:a', '128k'])
            .concat(['-shortest', '-y', 'output.' + info.ext]);
    }

    /* ───────────────────────── encoding session ───────────────────────── */

    function pngBytes(canvas) {
        return new Promise(function (resolve, reject) {
            if (canvas.convertToBlob) {
                canvas.convertToBlob({ type: 'image/png' })
                    .then(function (b) { return b.arrayBuffer(); })
                    .then(function (ab) { resolve(new Uint8Array(ab)); }, reject);
            } else {
                canvas.toBlob(function (b) {
                    if (!b) return reject(new Error('png encode failed'));
                    b.arrayBuffer().then(function (ab) { resolve(new Uint8Array(ab)); }, reject);
                }, 'image/png');
            }
        });
    }

    function pad5(n) { var s = String(n); while (s.length < 5) s = '0' + s; return s; }
    function pad3(n) { var s = String(n); while (s.length < 3) s = '0' + s; return s; }

    /* Alpha WebM via MediaRecorder (Chromium) — libvpx yuva420p OOBs, WebCodecs
       can't do alpha. Buffers frames, realtime-records to a transparent VP8/9
       WebM, then rewrites the timestamps and (if wanted) copy-muxes the source
       audio in ONE FFmpeg pass — `-c:v copy`, so libvpx never runs. Same
       session interface as the FFmpeg path.

       ⚠️ THE TIMESTAMPS MUST BE REWRITTEN, ALWAYS. MediaRecorder stamps frames
       by WALL CLOCK, and there is no way round that: frames pushed through a
       MediaStreamTrackGenerator with explicit timestamps are dropped unless
       they are also paced in real time (measured 2026-09-02: 90 frames written
       fast → 11 frames, 0.001 s; the same frames paced → 90 frames, 3.01 s).
       So on a loaded or slow machine the recorder simply falls behind and the
       clip comes out STRETCHED: a 3-second Telegram sticker measured 5.9, 7.9,
       12.6 and 20.2 s across four runs on a busy CPU — every one of them
       rejected by Telegram, and every overlay playing in slow motion. The fix
       is `setts`: after recording, every frame's PTS becomes round(N·1000/fps)
       exactly, and the file says what the frame count says. Verified on the
       20.2-second file: 88 frames → 30 fps → 2.90 s, alpha_mode intact. */
    function makeAlphaSession(proc, opts) {
        var rec = root.AlphaWebMRecorder.createSession({ width: opts.width, height: opts.height, fps: opts.fps });

        async function retime(videoBlob, wantAudio) {
            if (!proc._getFFmpeg || !proc._getFFmpeg()) { if (proc.init) await proc.init(function () {}); }
            var ff = proc._getFFmpeg && proc._getFFmpeg();
            if (!ff) throw new Error('ffmpeg-unavailable-for-retime');
            var fps = Math.max(1, opts.fps || 30);
            var ts = 'round(N*1000/' + fps + ')';
            var args = ['-i', 'alpha_v.webm'];
            if (wantAudio) args = args.concat(['-ss', String(opts.trimStart), '-t', String(opts.trimEnd - opts.trimStart), '-i', 'alpha_src']);
            args = args.concat(['-map', '0:v:0']);
            if (wantAudio) args = args.concat(['-map', '1:a:0?']);
            // alpha_mode is what tells a player the VP9 stream carries alpha;
            // restate it explicitly rather than trust the copy to carry the tag.
            args = args.concat(['-c:v', 'copy', '-bsf:v', 'setts=pts=' + ts + ':dts=' + ts,
                                '-metadata:s:v:0', 'alpha_mode=1']);
            // The recorder pushes the last picture twice more to carry the frames
            // its encoder holds back; one of them can come out too — cut to the count.
            if (videoBlob.frameCount > 0) args = args.concat(['-frames:v', String(videoBlob.frameCount)]);
            if (wantAudio) args = args.concat(['-c:a', 'libopus', '-b:a', '128k', '-shortest']);
            args = args.concat(['-y', 'alpha_out.webm']);

            await proc.writeFile('alpha_v.webm', new Uint8Array(await videoBlob.arrayBuffer()));
            if (wantAudio) await proc.writeFile('alpha_src', opts.sourceFile);
            try {
                var rc = await ff.exec(args);
                if (rc !== 0) throw new Error('retime exit ' + rc);
                var data = await ff.readFile('alpha_out.webm');
                if (!data || !data.length) throw new Error('retime empty');
                return new Blob([data.buffer], { type: 'video/webm' });
            } finally {
                ['alpha_v.webm', 'alpha_src', 'alpha_out.webm'].forEach(function (n) { try { proc.removeFile(n); } catch (_) {} });
            }
        }

        return {
            bitrate: adaptiveBitrate(opts.width, opts.height, opts.fps),
            addFrame: function (canvas) { return rec.addFrame(canvas); },
            flush: function () { return Promise.resolve(); },
            finalize: async function (onProgress) {
                if (onProgress) onProgress(0.05);
                var videoBlob = await rec.finalize(function (p) { if (onProgress) onProgress(0.05 + p * 0.8); });
                rec.cleanup();
                var wantAudio = !!(opts.keepAudio && opts.hasAudio && opts.sourceFile);
                if (onProgress) onProgress(0.88);
                try {
                    var fixed = await retime(videoBlob, wantAudio);
                    try { root.__ae && root.__ae('feature_use', { slug: 'video-bg-remover', action: wantAudio ? 'alpha_audio_mux_ok' : 'alpha_retime_ok' }); } catch (_) {}
                    if (onProgress) onProgress(1);
                    return { blob: fixed, name: 'output.webm', mime: 'video/webm', ext: 'webm' };
                } catch (err) {
                    // Fail-open: the recorder's own file is playable, only its
                    // clock is the machine's. Better a stretched clip than none.
                    try { root.__ae && root.__ae('feature_use', { slug: 'video-bg-remover', action: 'alpha_retime_fail', err: String((err && err.message) || err).slice(0, 120) }); } catch (_) {}
                    if (onProgress) onProgress(1);
                    return { blob: videoBlob, name: 'output.webm', mime: 'video/webm', ext: 'webm' };
                }
            },
            cleanup: function () { try { rec.cleanup(); } catch (_) {} }
        };
    }

    async function createSession(proc, opts) {
        // Alpha WebM: prefer the MediaRecorder path (Chromium) — the FFmpeg
        // libvpx yuva420p path below OOBs on real content. Non-Chromium (alpha
        // flattened) or unsupported → falls through to libvpx (broken; users
        // there still have working APNG/WebP/ProRes alpha formats).
        if (opts.format === 'webm-alpha' && root.AlphaWebMRecorder) {
            try {
                var sup = await root.AlphaWebMRecorder.isSupported({ fps: opts.fps });
                if (sup && sup.ok) {
                    try { root.__ae && root.__ae('feature_use', { slug: 'video-bg-remover', action: 'alpha_mediarecorder', codec: sup.codec }); } catch (_) {}
                    return makeAlphaSession(proc, opts);
                }
            } catch (_) {}
        }
        var info = formatInfo(opts.format);
        var ff = proc._getFFmpeg();
        var bitrate = opts.bitrate || adaptiveBitrate(opts.width, opts.height, opts.fps);

        var chunkCount = 0;         // frames in the current (unflushed) chunk
        var globalCount = 0;        // total frames added
        var segments = [];          // segment file names (segmented formats)
        var writtenPngs = [];       // global PNG names still in FS (non-segmented)

        async function encodeSegment() {
            if (chunkCount === 0) return;
            var segName = 'seg_' + pad3(segments.length) + '.' + info.segExt;
            var args = segmentArgs(opts.format, { fps: opts.fps, bitrate: bitrate, output: segName });
            var code = await ff.exec(args);
            if (code !== 0) throw new Error('segment encode failed (exit ' + code + ')');
            segments.push(segName);
            for (var i = 0; i < chunkCount; i++) {
                try { await proc.removeFile('f' + pad5(i) + '.png'); } catch (_) {}
            }
            chunkCount = 0;
        }

        async function addFrame(canvas) {
            var bytes = await pngBytes(canvas);
            if (info.segmented) {
                await proc.writeFile('f' + pad5(chunkCount) + '.png', bytes);
                chunkCount++;
                globalCount++;
                if (chunkCount >= CHUNK) await encodeSegment();
            } else {
                var name = 'f' + pad5(globalCount) + '.png';
                await proc.writeFile(name, bytes);
                writtenPngs.push(name);
                globalCount++;
            }
        }

        async function flush() {
            if (info.segmented) await encodeSegment();
        }

        async function finalize(onProgress) {
            if (onProgress) onProgress(0.1);
            var outName;

            if (!info.segmented) {
                // APNG / animated WebP — one pass over global PNGs.
                outName = 'output.' + info.ext;
                var code = await ff.exec(singlePassArgs(opts.format, { fps: opts.fps, output: outName }));
                if (code !== 0) throw new Error('encode failed (exit ' + code + ')');
            } else {
                // Concatenate segments (stream copy).
                if (onProgress) onProgress(0.4);
                var list = segments.map(function (s) { return "file '" + s + "'"; }).join('\n') + '\n';
                await proc.writeFile('concat.txt', new TextEncoder().encode(list));
                var cc = await ff.exec(concatArgs(opts.format));
                if (cc !== 0) throw new Error('concat failed (exit ' + cc + ')');

                var wantAudio = opts.keepAudio && opts.hasAudio && info.audio && opts.sourceFile;
                if (wantAudio) {
                    if (onProgress) onProgress(0.7);
                    await proc.writeFile('source_video', opts.sourceFile);
                    var mc = await ff.exec(muxArgs(opts.format, {
                        trimStart: opts.trimStart,
                        duration: opts.trimEnd - opts.trimStart,
                    }));
                    outName = (mc === 0) ? 'output.' + info.ext : 'silent.' + info.ext;
                } else {
                    outName = 'silent.' + info.ext;
                }
            }

            if (onProgress) onProgress(0.95);
            var data = await ff.readFile(outName);
            var blob = new Blob([data.buffer], { type: info.mime });
            await cleanup();
            if (onProgress) onProgress(1);
            return { blob: blob, name: outName, mime: info.mime, ext: info.ext };
        }

        async function cleanup() {
            for (var i = 0; i < segments.length; i++) {
                try { await proc.removeFile(segments[i]); } catch (_) {}
            }
            for (var p = 0; p < writtenPngs.length; p++) {
                try { await proc.removeFile(writtenPngs[p]); } catch (_) {}
            }
            for (var c = 0; c < chunkCount; c++) {
                try { await proc.removeFile('f' + pad5(c) + '.png'); } catch (_) {}
            }
            ['concat.txt', 'silent.' + info.ext, 'output.' + info.ext, 'source_video'].forEach(function (n) {
                try { proc.removeFile(n); } catch (_) {}
            });
        }

        return {
            addFrame: addFrame,
            flush: flush,
            finalize: finalize,
            cleanup: cleanup,
            bitrate: bitrate,
        };
    }

    root.VbrEncoder = {
        adaptiveBitrate: adaptiveBitrate,
        formatInfo: formatInfo,
        segmentArgs: segmentArgs,
        singlePassArgs: singlePassArgs,
        concatArgs: concatArgs,
        muxArgs: muxArgs,
        createSession: createSession,
        CHUNK: CHUNK,
    };
})(typeof window !== 'undefined' ? window : globalThis);
